import { Fragment } from "react";
import Tooltip from "@mui/material/Tooltip";

import { useAtomValue } from "jotai";

import { useState } from "react";
import { useHttpPost } from "./hooks";

import { getShortDescription } from "./storydat";

import { projectPickerState } from "./JotaiAtoms";

import { Box, Typography } from "@mui/material";

import { StoryItem } from "./StoryItem";

type StoryPopupProps = {
  storyMoniker: string;
  children: any;
  enterDelay: number;
  previewComponent?: (props: { item: StoryItem }) => JSX.Element | null;
  placement?:
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
};

type StoryPopupInnerProps = {
  hasStoryToShow: boolean;
  hover: boolean;
  story?: StoryItem;
  enterDelay: number;
  previewComponent?: (props: { item: StoryItem }) => JSX.Element | null;
  placement?: StoryPopupProps["placement"];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  children: any;
};

function StoryPopupInner({
  hasStoryToShow,
  hover,
  story,
  enterDelay,
  previewComponent,
  placement,
  onMouseEnter,
  onMouseLeave,
  children,
}: StoryPopupInnerProps) {
  if (!hasStoryToShow) {
    return (
      <Box
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        component={"span"}
      >
        {children}
      </Box>
    );
  }

  return (
    <Tooltip
      enterDelay={enterDelay || 100}
      enterNextDelay={enterDelay || 100}
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: "transparent",
            padding: 0,
            maxWidth: "400px",
          },
        },
        popper: {
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: document.getElementById("main-scroll-container"),
              },
            },
          ],
        },
      }}
      title={
        <Fragment>
          {previewComponent && <>{previewComponent({ item: story! })}</>}
          {!previewComponent && (
            <Typography fontSize={11}>{getShortDescription(story!)}</Typography>
          )}
        </Fragment>
      }
    >
      <Box
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        component={"span"}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

export function StoryPopup({
  storyMoniker,
  children,
  enterDelay,
  previewComponent,
  placement,
}: StoryPopupProps) {
  const [story, setStory] = useState<StoryItem>();
  const [hover, setHover] = useState<boolean>(false);
  const project = useAtomValue(projectPickerState);
  const httpPost = useHttpPost();

  const handleMouseEnter = () => {
    const request = async () => {
      try {
        const query =
          storyMoniker[0] === "[" ? storyMoniker.slice(1, -1) : storyMoniker;
        const req = { search: true, query: query, project: project };
        const resp = await httpPost("stories", req);
        const found = resp.data.search;
        if (found) {
          setStory(found[0]);
          return;
        }
      } catch (e) {
        setStory({
          error: "failure",
          PK: "failure",
          SK: "failure",
          SK2: "failure",
          SK3: "failure",
        });
      }
    };

    setHover(true);
    if (!story) {
      setTimeout(() => {
        setHover((prev) => {
          if (prev) {
            request();
          }
          return prev;
        });
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const hasStoryToShow = Boolean(story && !story.error);

  return (
    <StoryPopupInner
      hasStoryToShow={hasStoryToShow}
      hover={hover}
      story={story}
      enterDelay={enterDelay}
      previewComponent={previewComponent}
      placement={placement}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </StoryPopupInner>
  );
}
